import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

import React, { useEffect, useRef, useState } from 'react';

import {
    IconChevronSprite,
    IconChevronDown,
} from '$partials/icons/index.js';

import Image from '$partials/media/image.jsx';
import ModuleWalkthroughMobile from './walkthrough-mobile.jsx';
import Button from '$partials/ui/button.jsx';
import { useLocale } from '$contexts/locale.jsx';

gsap.registerPlugin(useGSAP);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleWalkthrough}
 */

const ModuleWalkthrough = ({
    eyebrow,
    heading,
    itemsCollection,
    itemsVariant,
    bodyCopy,
    mainCtaLabel,
    mainCtaLink,
    isArticlePage,
}) => {

    const { formatURI } = useLocale();

    const containerRef = useRef();

    const [activePanel, setActivePanel] = useState(0);
    const [windowSize, setWindowSize] = useState({ height: undefined, width: undefined });

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth !== windowSize.width) {
                setWindowSize({ height: window.innerHeight, width: window.innerWidth });
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [windowSize.width]);

    useGSAP(() => {
        const easing = 'power4.out';
        // Resizing panels
        gsap.to('.tab-panel[data-selected] .tab-panel-text', {
            height: 'auto',
            ease: easing,
            duration: 0.4
        });
        gsap.to('.tab-panel:not([data-selected]) .tab-panel-text', {
            height: 0,
            ease: easing,
            duration: 0.4
        });

        // Fading panel texts
        gsap.to('.tab-panel[data-selected] .tab-panel-text p', {
            opacity: 1,
            duration: 0.4
        });
        gsap.to('.tab-panel:not([data-selected]) .tab-panel-text p', {
            opacity: 0,
            duration: 0.2
        });

        // Cross-fading illustration
        gsap.to('.tab-panel:not([data-selected]) .tab-panel-illustration', {
            opacity: 0,
            duration: 0.3
        });
        gsap.to('.tab-panel[data-selected] .tab-panel-illustration', {
            opacity: 1,
            duration: 0.3,
        });
    }, {
        dependencies: [activePanel],
        scope: containerRef
    });

    const activePanelItem = itemsCollection?.items?.[activePanel];
    const ctaLabel = (activePanelItem?.ctaLabel && activePanelItem?.ctaLink) ? activePanelItem.ctaLabel : mainCtaLabel;
    const ctaLink = (activePanelItem?.ctaLabel && activePanelItem?.ctaLink) ? activePanelItem.ctaLink : mainCtaLink;

    return (
        <>
            <section className={`hidden md:block relative py-40${isArticlePage ? '' : ' lg:py-80'}`}>
                <div
                    className='layout-container layout-site-outer-padding'
                    ref={containerRef}>
                    {eyebrow && (
                        <p
                            className='type-style-eyebrow text-text-accent mb-12'>
                            {eyebrow}
                        </p>
                    )}
                    {heading && (
                        <h2 className={`${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} text-text-primary mb-10`}>
                            {heading}
                        </h2>
                    )}

                    {bodyCopy && (
                        <p className='type-style-body text-text-secondary md:w-5/12 lg:w-6/12'>
                            {bodyCopy}
                        </p>
                    )}

                    {itemsCollection?.items?.length > 0 && (
                        <TabGroup
                            vertical
                            onChange={(index) => {
                                setActivePanel(index);
                            }}>
                            <TabList>
                                <TabPanels className='flex relative mt-50'>
                                    <div className='flex flex-col md:w-5/12 lg:w-6/12 gap-16'>
                                        {itemsCollection.items.map((itm, idx) => {
                                            return (
                                                <div
                                                    className='bg-surface-raised hover:bg-surface-raised-hover mr-8 min-h-[84px] rounded-button'
                                                    key={idx}>
                                                    <Tab className='type-style-5 w-full py-[1.7rem] pl-24 text-left outline-none group inline-flex gap-16 items-center'>
                                                        <span
                                                            className='text-text-accent'>
                                                            {itemsVariant === 'Numbered' ?
                                                                String(idx + 1).padStart(2, '0') :
                                                                <IconChevronDown className='group-data-[selected]:-rotate-90 transition-transform' />
                                                            }
                                                        </span>
                                                        {itm?.heading && (
                                                            <span
                                                                className='group-hover:text-black transition-colors duration-100 group-data-[selected]:text-black text-dark-grey'>
                                                                {itm.heading}
                                                            </span>
                                                        )}
                                                    </Tab>
                                                    <TabPanel
                                                        className='tab-panel'
                                                        static={true}
                                                        unmount={false}>
                                                        <div
                                                            className='tab-panel-text overflow-clip'
                                                            style={{
                                                                height: idx === 0 ? 'auto' : 0
                                                            }}>
                                                            <div className='pl-24 pr-30 pb-30'>
                                                                {itm?.copy && (
                                                                    <p
                                                                        className='text-grey-off-black'>
                                                                        {itm.copy}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className='tab-panel-illustration absolute top-0 right-0 md:w-7/12 lg:w-6/12 pointer-events-none select-none'>
                                                            <div className='pl-8 w-full'>
                                                                {itm?.media?.contentType?.includes('video/') ?
                                                                    (
                                                                        <>
                                                                            {activePanel === idx && (
                                                                                <video
                                                                                    autoPlay
                                                                                    loop
                                                                                    muted
                                                                                    playsInline
                                                                                    className='w-full h-full object-cover aspect-square rounded-button'
                                                                                    src={itm?.media?.src}
                                                                                    width={'100%'} />
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <Image
                                                                            {...itm?.media}
                                                                            className='h-full w-full object-center object-cover select-none pointer-events-none aspect-square rounded-button'
                                                                            containerClassName='w-full h-full relative'
                                                                            height='100%'
                                                                            loading=''
                                                                            loadWidth={998}
                                                                            width='100%' />
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                    </TabPanel>
                                                </div>
                                            );
                                        })}

                                        {/* Button */}
                                        {ctaLabel && ctaLink && (
                                            <div>
                                                <Button
                                                    as='a'
                                                    className='button button--secondary-alt'
                                                    href={formatURI(ctaLink)}
                                                    type='button'>
                                                    <span className='button__label--animate'>
                                                        <span>
                                                            {ctaLabel}
                                                        </span>
                                                    </span>
                                                    <span className='button__icon button__icon--animate'>
                                                        <IconChevronSprite />
                                                    </span>
                                                </Button>
                                            </div>
                                        )}
                                        {/* Mobile */}
                                    </div>
                                    {/* Spacer */}
                                    <div className='md:w-7/12 lg:w-6/12 pl-8'>
                                        <div className='aspect-square'></div>
                                    </div>
                                </TabPanels>
                            </TabList>
                        </TabGroup>
                    )}

                </div>
            </section>

            {(windowSize.width < 1280) && (
                <ModuleWalkthroughMobile
                    bodyCopy={bodyCopy}
                    eyebrow={eyebrow}
                    heading={heading}
                    isArticlePage={isArticlePage}
                    itemsCollection={itemsCollection}
                    itemsVariant={itemsVariant}
                    mainCtaLabel={mainCtaLabel}
                    mainCtaLink={mainCtaLink}/>
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default ModuleWalkthrough;
